import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  fetchProjectAnalyticsFilters,
  fetchProjectAnalyticsInsights,
  fetchProjectCompletionBreakdown,
  fetchProjectAnalyticsDrilldown,
  downloadProjectAnalyticsExport,
} from '../../api/analyticsApi'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function daysAgoIso(days) {
  const current = new Date()
  current.setDate(current.getDate() - Math.max(0, Number(days || 0)))
  return current.toISOString().slice(0, 10)
}

function toDropdownOptions(items = [], emptyLabel = 'All') {
  return [{ value: '', label: emptyLabel }, ...items.map((item) => ({ value: String(item.id), label: item.name }))]
}

function toValueOptions(items = [], emptyLabel = 'All') {
  return [{ value: '', label: emptyLabel }, ...items.map((item) => ({ value: String(item), label: String(item) }))]
}

export function useProjectAnalytics() {
  const [projectId, setProjectId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [customer, setCustomer] = useState('')
  const [projectManagerId, setProjectManagerId] = useState('')
  const [status, setStatus] = useState('')
  const [presetDays, setPresetDays] = useState('30')
  const [dateFrom, setDateFrom] = useState(daysAgoIso(29))
  const [dateTo, setDateTo] = useState(todayIso())

  const [projects, setProjects] = useState([])
  const [departments, setDepartments] = useState([])
  const [datePresets, setDatePresets] = useState([])
  const [customers, setCustomers] = useState([])
  const [projectManagers, setProjectManagers] = useState([])
  const [statuses, setStatuses] = useState([])
  const [insights, setInsights] = useState(null)
  const [breakdown, setBreakdown] = useState(null)

  const [isLoadingFilters, setIsLoadingFilters] = useState(true)
  const [isLoadingInsights, setIsLoadingInsights] = useState(true)
  const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false)
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false)
  const [error, setError] = useState('')
  const [breakdownError, setBreakdownError] = useState('')
  const [costBillingView, setCostBillingView] = useState('chart')
  const [costBillingGroup, setCostBillingGroup] = useState('monthly')
  const [resourceLoadView, setResourceLoadView] = useState('chart')
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [drillTitle, setDrillTitle] = useState('Project Details')
  const [drillLevel, setDrillLevel] = useState('project')
  const [drillPeriodFrom, setDrillPeriodFrom] = useState(null)
  const [drillPeriodTo, setDrillPeriodTo] = useState(null)
  const [drilldown, setDrilldown] = useState(null)
  const [isLoadingDrilldown, setIsLoadingDrilldown] = useState(false)
  const [drilldownError, setDrilldownError] = useState('')
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    let active = true
    async function loadFilters() {
      setIsLoadingFilters(true)
      try {
        const payload = await fetchProjectAnalyticsFilters()
        if (!active) return
        setProjects(Array.isArray(payload?.projects) ? payload.projects : [])
        setDepartments(Array.isArray(payload?.departments) ? payload.departments : [])
        setDatePresets(Array.isArray(payload?.datePresets) ? payload.datePresets : [])
        setCustomers(Array.isArray(payload?.customers) ? payload.customers : [])
        setProjectManagers(Array.isArray(payload?.projectManagers) ? payload.projectManagers : [])
        setStatuses(Array.isArray(payload?.statuses) ? payload.statuses : [])
      } catch (requestError) {
        if (!active) return
        setProjects([])
        setDepartments([])
        setDatePresets([])
        setError(extractApiErrorMessage(requestError, 'Failed to load project analytics filters.'))
      } finally {
        if (active) setIsLoadingFilters(false)
      }
    }
    loadFilters()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!projectId) return
    const isValid = projects.some((item) => String(item.id) === String(projectId))
    if (!isValid) {
      setProjectId('')
    }
  }, [projectId, projects])

  useEffect(() => {
    let active = true
    async function loadInsights() {
      setIsLoadingInsights(true)
      setError('')
      try {
        const payload = await fetchProjectAnalyticsInsights({
          projectId: projectId || undefined,
          departmentId: departmentId || undefined,
          customer: customer || undefined,
          projectManagerId: projectManagerId || undefined,
          status: status || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        })
        if (!active) return
        setInsights(payload || {})
      } catch (requestError) {
        if (!active) return
        setInsights(null)
        setError(extractApiErrorMessage(requestError, 'Failed to load project analytics insights.'))
      } finally {
        if (active) setIsLoadingInsights(false)
      }
    }
    loadInsights()
    return () => {
      active = false
    }
  }, [projectId, departmentId, customer, projectManagerId, status, dateFrom, dateTo])

  async function openBreakdown() {
    setIsBreakdownOpen(true)
    setIsLoadingBreakdown(true)
    setBreakdownError('')
    try {
      const payload = await fetchProjectCompletionBreakdown({
        projectId: projectId || undefined,
        departmentId: departmentId || undefined,
        customer: customer || undefined,
        projectManagerId: projectManagerId || undefined,
        status: status || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })
      setBreakdown(payload || {})
    } catch (requestError) {
      setBreakdown(null)
      setBreakdownError(extractApiErrorMessage(requestError, 'Failed to load completion breakdown.'))
    } finally {
      setIsLoadingBreakdown(false)
    }
  }

  function queryParams(overrides = {}) {
    const periodFrom = drillPeriodFrom || dateFrom
    const periodTo = drillPeriodTo || dateTo
    return {
      projectId: projectId || undefined,
      departmentId: departmentId || undefined,
      customer: customer || undefined,
      projectManagerId: projectManagerId || undefined,
      status: status || undefined,
      dateFrom: periodFrom || undefined,
      dateTo: periodTo || undefined,
      ...overrides,
    }
  }

  async function openDrilldown(title = 'Project Details', level = 'project', periodFrom = null, periodTo = null) {
    setIsDetailsOpen(true)
    setDrillTitle(title)
    setDrillLevel(level)
    setDrillPeriodFrom(periodFrom)
    setDrillPeriodTo(periodTo)
    setIsLoadingDrilldown(true)
    setDrilldownError('')
    try {
      const periodOverrides = periodFrom && periodTo ? { dateFrom: periodFrom, dateTo: periodTo } : {}
      setDrilldown(await fetchProjectAnalyticsDrilldown(queryParams({ level, ...periodOverrides })))
    } catch (requestError) {
      setDrilldown(null)
      setDrilldownError(extractApiErrorMessage(requestError, 'Failed to load drill-down data.'))
    } finally {
      setIsLoadingDrilldown(false)
    }
  }

  async function exportCurrentDrilldown(format = 'csv') {
    setIsExporting(true)
    try {
      const blob = await downloadProjectAnalyticsExport(queryParams({ level: drillLevel, format }))
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `project_analytics_${drillLevel}.${format}`
      anchor.click()
      window.URL.revokeObjectURL(url)
    } catch (requestError) {
      setDrilldownError(extractApiErrorMessage(requestError, 'Failed to export project analytics.'))
    } finally {
      setIsExporting(false)
    }
  }

  function applyPreset(daysValue) {
    const parsed = Number(daysValue || 30)
    const normalizedDays = Number.isFinite(parsed) && parsed > 0 ? parsed : 30
    setPresetDays(String(normalizedDays))
    setDateTo(todayIso())
    setDateFrom(daysAgoIso(normalizedDays - 1))
  }

  const projectOptions = useMemo(() => toDropdownOptions(projects, 'All Strategic Projects'), [projects])
  const departmentOptions = useMemo(() => toDropdownOptions(departments, 'All Departments'), [departments])
  const customerOptions = useMemo(() => toValueOptions(customers, 'All Customers'), [customers])
  const projectManagerOptions = useMemo(() => toDropdownOptions(projectManagers, 'All Managers'), [projectManagers])
  const statusOptions = useMemo(() => toValueOptions(statuses, 'All Statuses'), [statuses])
  const presetOptions = useMemo(
    () =>
      (datePresets.length ? datePresets : [{ label: 'Last 30 Days', days: 30 }]).map((item) => ({
        value: String(item.days),
        label: item.label,
      })),
    [datePresets],
  )

  return {
    state: {
      projectId,
      departmentId,
      customer,
      projectManagerId,
      status,
      presetDays,
      dateFrom,
      dateTo,
      projectOptions,
      departmentOptions,
      customerOptions,
      projectManagerOptions,
      statusOptions,
      presetOptions,
      insights,
      breakdown,
      isBreakdownOpen,
      isLoadingBreakdown,
      breakdownError,
      costBillingView,
      costBillingGroup,
      resourceLoadView,
      isDetailsOpen,
      drillTitle,
      drillLevel,
      drillPeriodFrom,
      drillPeriodTo,
      drilldown,
      isLoadingDrilldown,
      drilldownError,
      isExporting,
      isLoading: isLoadingFilters || isLoadingInsights,
      isLoadingFilters,
      isLoadingInsights,
      error,
    },
    actions: {
      setProjectId,
      setDepartmentId,
      setCustomer,
      setProjectManagerId,
      setStatus,
      setDateFrom,
      setDateTo,
      applyPreset,
      setCostBillingView,
      setCostBillingGroup,
      setResourceLoadView,
      openBreakdown,
      closeBreakdown: () => setIsBreakdownOpen(false),
      openDetails: openDrilldown,
      openJobs: (periodFrom, periodTo) => openDrilldown('Project Jobs', 'job', periodFrom, periodTo),
      openTasks: (periodFrom, periodTo) => openDrilldown('Project Tasks', 'task', periodFrom, periodTo),
      openTimesheets: (periodFrom, periodTo) => openDrilldown('Activity Timesheets', 'timesheet', periodFrom, periodTo),
      openFinancial: (periodFrom, periodTo) => openDrilldown('Financial Invoices & Receivables', 'financial', periodFrom, periodTo),
      closeDetails: () => {
        setIsDetailsOpen(false)
        setDrillPeriodFrom(null)
        setDrillPeriodTo(null)
      },
      exportCurrentDrilldown,
      resetFilters: () => {
        setProjectId('')
        setDepartmentId('')
        setCustomer('')
        setProjectManagerId('')
        setStatus('')
        applyPreset('30')
      },
    },
  }
}
